import React, { useReducer, useState } from 'react';
import { Approval } from '../types/campaigns';

export type CampaignData = {
    campaign: any;
    patient: any;
    csc: any;
    campaign_request: any;
    notes: any;
    campaignPhotoInfo: any;
    coordinator: any;
    financial_coordinator?: any;
    count?: number;
    application: any;
    approval?: Approval | null;
} | null;

export type CampaignAction =
    | { type: 'set_campaign'; payload: CampaignData }
    | { type: 'set_financial_coordinator'; payload: any }
    | { type: 'set_csc'; payload: any }
    | { type: 'set_campaign_status'; payload: { id?: number; code?: string; name?: string } }
    | { type: 'set_wordpress_info'; payload: { wordpress_id?: number | string | null; wordpress_permalink?: string | null } }
    | { type: 'update_campaign_request'; payload: { campaign_request: any } }
    | { type: 'set_medical_info'; payload: { patient?: any; application?: any } }
    | { type: 'set_supporter_count'; payload: number }
    | { type: 'add_team_member'; payload: any }
    | { type: 'update_team_member'; payload: any }
    | { type: 'remove_team_member'; payload: { id: number } };

export interface CampaignState {
    campaignData: CampaignData;
}

const initialState: CampaignState = {
    campaignData: null,
};

export const CampaignContext = React.createContext<CampaignState | null>(null);

const CampaignReducer = (state: CampaignState, action: CampaignAction) => {
    switch (action.type) {
        case 'set_campaign':
            return { ...state, campaignData: action.payload };
        case 'set_financial_coordinator':
            return {
                ...state,
                campaignData: {
                    ...state.campaignData,
                    financial_coordinator: action.payload,
                },
            };
        case 'set_csc':
            return {
                ...state,
                campaignData: {
                    ...state.campaignData,
                    csc: action.payload,
                },
            };
        case 'set_campaign_status':
            return {
                ...state,
                campaignData: state.campaignData
                    ? {
                          ...state.campaignData,
                          campaign: {
                              ...state.campaignData.campaign,
                              campaign_status: action.payload,
                          },
                      }
                    : null,
            };
        case 'set_wordpress_info':
            return {
                ...state,
                campaignData: state.campaignData
                    ? {
                          ...state.campaignData,
                          campaign: {
                              ...state.campaignData.campaign,
                              wordpress_id: action.payload.wordpress_id,
                              wordpress_permalink: action.payload.wordpress_permalink,
                          },
                      }
                    : null,
            };
        case 'update_campaign_request':
            return {
                ...state,
                campaignData: {
                    ...state.campaignData,
                    campaign_request: action.payload.campaign_request,
                },
            };
        // Patch just the medical-info slices from a save response so the
        // Medical Info section repaints immediately, instead of waiting on the
        // full campaign refetch. The endpoints return the same
        // PatientDetailSerializer / ApplicationSerializer shapes the campaign
        // detail endpoint does, so these can replace their slices outright.
        case 'set_medical_info':
            return {
                ...state,
                campaignData: state.campaignData
                    ? {
                          ...state.campaignData,
                          patient:
                              action.payload.patient ?? state.campaignData.patient,
                          application:
                              action.payload.application ??
                              state.campaignData.application,
                      }
                    : null,
            };
        case 'set_supporter_count':
            return {
                ...state,
                campaignData: state.campaignData
                    ? {
                          ...state.campaignData,
                          count: action.payload,
                      }
                    : null,
            };
        case 'add_team_member':
            return {
                ...state,
                campaignData: state.campaignData
                    ? {
                          ...state.campaignData,
                          campaign: {
                              ...state.campaignData.campaign,
                              team_members: [
                                  ...(state.campaignData.campaign?.team_members || []),
                                  action.payload,
                              ],
                          },
                      }
                    : null,
            };
        case 'update_team_member':
            return {
                ...state,
                campaignData: state.campaignData
                    ? {
                          ...state.campaignData,
                          campaign: {
                              ...state.campaignData.campaign,
                              team_members: (state.campaignData.campaign?.team_members || []).map(
                                  (member: any) =>
                                      member.id === action.payload.id ? action.payload : member
                              ),
                          },
                      }
                    : null,
            };
        case 'remove_team_member':
            return {
                ...state,
                campaignData: state.campaignData
                    ? {
                          ...state.campaignData,
                          campaign: {
                              ...state.campaignData.campaign,
                              team_members: (state.campaignData.campaign?.team_members || []).filter(
                                  (member: any) => member.id !== action.payload.id
                              ),
                          },
                      }
                    : null,
            };
        default:
            return state;
    }
};

const CampaignProvider = ({ children }: any) => {
    const [state, dispatch] = useReducer(CampaignReducer, initialState);

    return (
        <CampaignContext.Provider value={[state, dispatch]}>
            {children}
        </CampaignContext.Provider>
    );
};

export default CampaignProvider;
